// Puts a guest booking on the account once its guest signs in.
//
// The account's booking list (/me/reservations) shows reservations whose
// `userId` is the account. A booking made as a guest has none, so signing in
// with the same email did not, on its own, make it appear. The API already
// has the fix the mobile app uses: POST /profile/my-bookings/claim with the
// booking's reference and email, the same pair that already opens it through
// the public lookup. It only ever claims a booking with no owner.
//
// The thank-you page's "Keep this booking" card leaves the pair in
// sessionStorage when the guest starts signing in; this claims it as soon as
// the session exists, on this page or on the one Google and Apple return to.
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "../../../lib/auth";
import { api } from "../../../lib/api";
import { accountKeys } from "../constants/query-keys";
import { readPendingClaims, removePendingClaim } from "../../auth/lib/post-sign-in";

type ClaimClient = {
  profile: {
    "my-bookings": {
      claim: {
        post: (body: { items: { reference: string; email: string }[] }) => Promise<{
          data?: { claimed?: string[] } | null;
          error?: unknown;
        }>;
      };
    };
  };
};

export async function claimPendingBookings(): Promise<string[]> {
  const items = readPendingClaims();
  if (items.length === 0) return [];
  // Off the Eden chain, like the mobile app's call to the same route.
  const res = await (api as unknown as ClaimClient).profile["my-bookings"].claim.post({ items });
  if (res?.error) throw new Error("claim failed");
  // A pair the API did not claim (already on an account, or not a match) will
  // not succeed on a retry either, so every pair sent is done with.
  for (const item of items) removePendingClaim(item.reference);
  return res?.data?.claimed ?? [];
}

export function GuestBookingClaimer() {
  const { data } = useSession() as { data?: { user?: { id?: string } } | null };
  const userId = data?.user?.id ?? null;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    claimPendingBookings()
      .then((claimed) => {
        if (!cancelled && claimed.length > 0) {
          void queryClient.invalidateQueries({ queryKey: accountKeys.reservations() });
        }
      })
      .catch(() => {
        // Left in storage; the next sign-in in this tab tries again.
      });
    return () => {
      cancelled = true;
    };
  }, [userId, queryClient]);

  return null;
}
